import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";
import { RupiahInput } from "@/components/RupiahInput";

const jakartaToday = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Jakarta" });

// One visible choice per postable GL. Legacy category tokens stay valid in the backend
// for historical expenses and old clients, but are not offered to new operators.
const CATEGORIES = [
  { value: "ADVERTISING", code: "6201", label: "6201 — IKLAN EKSTERNAL / ADVERTISING" },
  { value: "PAYROLL", code: "6310", label: "6310 — GAJI / THR / BONUS / INSENTIF KARYAWAN" },
  { value: "RENT", code: "6320", label: "6320 — SEWA" },
  { value: "OFFICE", code: "6330", label: "6330 — ADMIN & OPERASIONAL" },
  { value: "LISTRIK", code: "6341", label: "6341 — LISTRIK" },
  { value: "PAM", code: "6342", label: "6342 — AIR / PAM" },
  { value: "INTERNET", code: "6343", label: "6343 — INTERNET / KOMUNIKASI" },
  { value: "IPL", code: "6344", label: "6344 — IPL / FASILITAS" },
  { value: "SHIPPING", code: "6350", label: "6350 — ONGKIR PERUSAHAAN" },
  { value: "PACKAGING", code: "6360", label: "6360 — KEMASAN OPERASIONAL" },
];
const LEGACY_CANONICAL = {
  OPERATIONAL: "OFFICE", MISC: "OFFICE", PENGELUARAN: "OFFICE",
  UMUM: "OFFICE", UTILITIES: "LISTRIK",
};
const VALID_CATEGORIES = CATEGORIES.map((item) => item.value);

export function ExpensePanel({ onDone, accounts: externalAccounts = null, fundRows: externalFundRows = null }) {
  const [localAccounts, setLocalAccounts] = useState([]);
  const [localFundRows, setLocalFundRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [src, setSrc] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [fundIdentity, setFundIdentity] = useState("MODAL");
  const [bankDebitDate, setBankDebitDate] = useState(jakartaToday);
  const [payee, setPayee] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const hasExternalBalances = Array.isArray(externalAccounts) && Array.isArray(externalFundRows);
  const accounts = hasExternalBalances ? externalAccounts : localAccounts;
  const fundRows = hasExternalBalances ? externalFundRows : localFundRows;

  const load = useCallback(() => {
    if (!hasExternalBalances) {
      Promise.all([api.get("/accounts"), api.get("/reports/fund-health")])
        .then(([a, f]) => {
          setLocalAccounts(Array.isArray(a.data) ? a.data : []);
          setLocalFundRows(Array.isArray(f.data?.rows) ? f.data.rows : []);
        });
    }
    api.get("/finance/expenses-effective").then(({ data }) => setRows(data.rows || []));
  }, [hasExternalBalances]);
  useEffect(() => { load(); setKey(crypto.randomUUID()); }, [load]);

  const fundMap = new Map(fundRows.map((r) => [r.account_id, r]));
  const banks = accounts
    .filter((a) => a.account_type === "BANK" && String(a.is_active) === "true")
    .map((a) => ({ ...a, ...(fundMap.get(a.account_id) || {}) }));

  const submit = () => {
    setErr(""); setOk("");
    if (!src) return setErr("Akun BANK belum dipilih");
    if (!VALID_CATEGORIES.includes(category)) return setErr("Pilih KATEGORI / COA biaya terlebih dahulu.");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bankDebitDate) || bankDebitDate > jakartaToday()) return setErr("Tanggal Debit Bank wajib diisi dan tidak boleh di masa depan.");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return setErr("Nominal tidak valid");
    setBusy(true);
    api.post("/finance/expenses", { idempotency_key: key, account_id: src, amount: amt, category, fund_identity: fundIdentity, bank_debit_date: bankDebitDate, payee: payee || null, reference: reference || null, note: note || null })
      .then(({ data }) => {
        setOk(`Biaya tercatat. Ref: ${data.id}`);
        setAmount(""); setPayee(""); setReference(""); setNote(""); setCategory(""); setBankDebitDate(jakartaToday()); setKey(crypto.randomUUID());
        load(); if (onDone) onDone();
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mencatat biaya"))
      .finally(() => setBusy(false));
  };

  const reverse = (id) => {
    const reason = window.prompt("Alasan pembatalan biaya:");
    if (!reason || !reason.trim()) return;
    api.post(`/finance/expenses/${id}/reverse`, { reason: reason.trim() })
      .then(() => { setOk("Biaya dibatalkan (reversed)"); load(); if (onDone) onDone(); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membatalkan"));
  };

  const correct = (row) => {
    const categoryNext = window.prompt(`Kategori koreksi (kode: ${VALID_CATEGORIES.join(", ")}):`, LEGACY_CANONICAL[row.category] || row.category || "");
    if (categoryNext === null) return;
    const cat = categoryNext.trim().toUpperCase();
    if (!VALID_CATEGORIES.includes(cat)) return setErr("Kategori koreksi tidak valid");
    const payeeNext = window.prompt("Penerima / Payee:", row.payee || "");
    if (payeeNext === null) return;
    const refNext = window.prompt("Referensi:", row.reference || "");
    if (refNext === null) return;
    const noteNext = window.prompt("Catatan:", row.note || "");
    if (noteNext === null) return;
    const reason = window.prompt("Alasan koreksi:");
    if (!reason || !reason.trim()) return;
    setBusy(true); setErr(""); setOk("");
    api.post(`/finance/expenses/${row.id}/correct`, {
      correction_key: crypto.randomUUID(), category: cat,
      payee: payeeNext || null, reference: refNext || null, note: noteNext || null,
      reason: reason.trim(),
    })
      .then(() => { setOk("Biaya sudah dikoreksi. Transaksi asli tetap tersimpan untuk audit."); load(); if (onDone) onDone(); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengoreksi biaya"))
      .finally(() => setBusy(false));
  };

  return (
    <div data-testid="expense-view" style={{ display: "grid", gap: 16 }}>
      <section className="panel" data-testid="expense-form" style={{ padding: 18 }}>
        <h3 className="dash-h" style={{ marginTop: 0 }}>Biaya Operasional (BANK → Keluar)</h3>
        {err && <div className="alert-error" data-testid="expense-error">{err}</div>}
        {ok && <div className="alert-ok" data-testid="expense-success">{ok}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 18, alignItems: "start" }}>
          <div>
            <label className="field-label">DARI BANK <span style={{ color: "#dc2626" }}>*</span></label>
            <select className="field-input" value={src} onChange={(e) => setSrc(e.target.value)} data-testid="expense-source">
              <option value="">— Pilih BANK —</option>
              {banks.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">KATEGORI / COA <span style={{ color: "#dc2626" }}>*</span></label>
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-category">
              <option value="">— Pilih Kategori / COA —</option>
              {CATEGORIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            {category === "PAYROLL" && <div className="hint" style={{ marginTop: 5 }}>
              Isi Catatan: gaji, THR, bonus, atau insentif. Jangan catat ulang jika sudah melalui Payout.
            </div>}
            {category === "ADVERTISING" && <div className="hint" style={{ marginTop: 5 }}>
              Khusus iklan dibayar langsung dari BANK; bukan iklan marketplace yang sudah terjurnal.
            </div>}
          </div>
          <div>
            <label className="field-label">PENERIMA / PAYEE</label>
            <input className="field-input" value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Nama penerima / payee" data-testid="expense-payee" />
          </div>

          <div>
            <label className="field-label">IDENTITAS DANA</label>
            <select className="field-input" value={fundIdentity} onChange={(e) => setFundIdentity(e.target.value)} data-testid="expense-fund-identity">
              <option value="MODAL">MODAL</option>
              <option value="PROFIT">PROFIT</option>
              <option value="UNCLASSIFIED">UNCLASSIFIED</option>
            </select>
            {src && (() => {
              const sel = banks.find((a) => a.account_id === src);
              if (!sel) return null;
              const avail = fundIdentity === "MODAL" ? sel.modal_available : fundIdentity === "PROFIT" ? sel.profit_available : sel.unclassified_available;
              return <div className="mono" data-testid="expense-fund-available" style={{ fontSize: 12, marginTop: 4, fontWeight: 700 }}>Tersedia: {formatIDR(avail)}</div>;
            })()}
          </div>
          <div>
            <label className="field-label">NOMINAL <span style={{ color: "#dc2626" }}>*</span></label>
            <RupiahInput className="field-input" value={amount} onChange={setAmount} placeholder="0" data-testid="expense-amount" />
          </div>
          <div>
            <label className="field-label">REFERENSI</label>
            <input className="field-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="No. invoice / ref" data-testid="expense-reference" />
          </div>

          <div>
            <label className="field-label">TANGGAL DEBIT BANK <span style={{ color: "#dc2626" }}>*</span></label>
            <input className="field-input" type="date" value={bankDebitDate} max={jakartaToday()}
              onChange={(e) => setBankDebitDate(e.target.value)} data-testid="expense-bank-debit-date"
              aria-describedby="expense-bank-debit-hint" />
            <div id="expense-bank-debit-hint" className="hint" style={{ marginTop: 5 }}>
              Sesuai mutasi bank. Saat ini tanggal jurnal belum mengikuti tanggal debit; aturan periode tutup buku masih disiapkan. Tanggal input ERP tetap diaudit.
            </div>
          </div>
          <div>
            <label className="field-label">CATATAN</label>
            <input className="field-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" data-testid="expense-note" />
          </div>
          <div style={{ display: "flex", alignItems: "end" }}>
            <button className="btn-mini on" disabled={busy} onClick={submit} data-testid="expense-submit-btn" style={{ padding: "10px 24px", minHeight: 40 }}>
              {busy ? "Menyimpan…" : "Catat Biaya"}
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
